import { useCallback, useEffect, useRef, useState } from "react";

// Push-to-talk запис от микрофона през getUserMedia + MediaRecorder.
//
// ЗАЩО не Web Speech API (webkitSpeechRecognition): то е стабилно само в
// Chrome за десктоп — в Chrome за iPhone го няма, в инсталираното iOS PWA
// гърми при старт, в Android Chrome спира сам след няколко секунди тишина.
// MediaRecorder работи в Safari/iOS (вкл. PWA и Chrome за iPhone, които са
// WebKit), Android Chrome, Firefox и десктопите. Речта се превръща в текст на
// сървъра (POST /voice/transcribe), затова hook-ът връща само звука.
//
// Жизнен цикъл на един запис: start() (от pointerdown — getUserMedia иска
// потребителски жест, особено в iOS) → stop() (от pointerup/pointercancel) →
// резултат. getUserMedia е асинхронно: ако бутонът бъде пуснат, преди
// стриймът да е готов (кратко натискане, или системният диалог за
// разрешение е „откраднал" пръста), записът се отказва тихо и stop() връща
// { kind: "empty" } — модалът остава в изходната стъпка и чака ново натискане.

export type AudioRecorderErrorKind = "denied" | "other";

export interface RecordedAudio {
  blob: Blob;
  /** Чист MIME без параметри, напр. `audio/webm` или `audio/mp4` — това
      заминава като Content-Type към BE-то. */
  mimeType: string;
  durationMs: number;
}

export type RecordingOutcome =
  | { kind: "audio"; audio: RecordedAudio }
  | { kind: "error"; error: AudioRecorderErrorKind }
  // Нищо за пращане: кратко натискане, отказ преди старт, празен blob
  | { kind: "empty" };

export interface UseAudioRecorderResult {
  /** false = няма getUserMedia/MediaRecorder (стар браузър или НЕ secure
      context — микрофонът работи само през HTTPS или localhost). */
  supported: boolean;
  /** true от натискането до края на записа (включително докато чакаме
      разрешението за микрофона) — за индикацията на бутона. */
  recording: boolean;
  /** Секунди от началото на записа, за брояча в UI. */
  elapsedSeconds: number;
  start: () => void;
  stop: () => Promise<RecordingOutcome>;
  /** Спира и изхвърля текущия запис (затваряне на модала). */
  cancel: () => void;
}

// Записи под този праг са случайно докосване, не поръчка.
const MIN_DURATION_MS = 300;
// Предпазител: push-to-talk поръчка е секунди; така записът остава малък
// (< 1 MB) и никога не опира в тавана на BE-то/ingress-а.
const MAX_DURATION_MS = 60_000;

// По предпочитание: Opus в WebM (Chrome/Android/Firefox, най-компактно),
// после MP4/AAC (Safari/iOS). OpenAI приема и двата.
const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const pickMimeType = (): string | undefined =>
  PREFERRED_MIME_TYPES.find((candidate) =>
    MediaRecorder.isTypeSupported(candidate),
  );

const stripMimeParameters = (mimeType: string): string =>
  mimeType.split(";")[0].trim().toLowerCase();

const isRecorderSupported = (): boolean =>
  typeof window !== "undefined" &&
  typeof MediaRecorder !== "undefined" &&
  typeof navigator !== "undefined" &&
  navigator.mediaDevices?.getUserMedia != null;

const errorKindOf = (error: unknown): AudioRecorderErrorKind => {
  const name = (error as { name?: string } | null)?.name;
  return name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError"
    ? "denied"
    : "other";
};

// Всичко за ЕДИН запис — живее в ref, за да не зависят start/stop от
// рендерите (state-ът е само за UI).
interface RecordingSession {
  stream: MediaStream | null;
  recorder: MediaRecorder | null;
  chunks: Blob[];
  startedAt: number;
  // Пуснат бутон преди стриймът да е готов → записът не стартира изобщо
  cancelled: boolean;
  // Грешка от getUserMedia/MediaRecorder — stop() я връща
  error: AudioRecorderErrorKind | null;
  // Резултатът, когато записът е спрял сам (таванът по време) — stop() го
  // връща, вместо да чака нов onstop
  finished: RecordingOutcome | null;
  maxDurationTimer: number | null;
}

const releaseStream = (session: RecordingSession) => {
  session.stream?.getTracks().forEach((track) => track.stop());
  session.stream = null;
};

export const useAudioRecorder = (): UseAudioRecorderResult => {
  const [recording, setRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const sessionRef = useRef<RecordingSession | null>(null);

  const supported = isRecorderSupported();

  // Спира recorder-а и връща обещание за събрания звук; ако той не е тръгнал
  // (стриймът още се чака или е отказан), връща "empty"/"error" веднага.
  const finishSession = useCallback(
    (session: RecordingSession): Promise<RecordingOutcome> => {
      if (session.maxDurationTimer != null) {
        window.clearTimeout(session.maxDurationTimer);
        session.maxDurationTimer = null;
      }
      const recorder = session.recorder;
      if (!recorder || recorder.state === "inactive") {
        releaseStream(session);
        if (session.error) return Promise.resolve({ kind: "error", error: session.error });
        return Promise.resolve(session.finished ?? { kind: "empty" });
      }
      return new Promise((resolve) => {
        recorder.onstop = () => {
          releaseStream(session);
          const durationMs = Date.now() - session.startedAt;
          const chunkType = session.chunks[0]?.type;
          const mimeType = stripMimeParameters(
            recorder.mimeType || chunkType || "",
          );
          const blob = new Blob(session.chunks, { type: mimeType });
          if (blob.size === 0 || durationMs < MIN_DURATION_MS || !mimeType) {
            resolve({ kind: "empty" });
            return;
          }
          resolve({ kind: "audio", audio: { blob, mimeType, durationMs } });
        };
        recorder.stop();
      });
    },
    [],
  );

  const start = useCallback(() => {
    if (!isRecorderSupported() || sessionRef.current) return;

    const session: RecordingSession = {
      stream: null,
      recorder: null,
      chunks: [],
      startedAt: Date.now(),
      cancelled: false,
      error: null,
      finished: null,
      maxDurationTimer: null,
    };
    sessionRef.current = session;
    setRecording(true);
    setElapsedSeconds(0);

    void (async () => {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error: unknown) {
        session.error = errorKindOf(error);
        return;
      }
      // Пуснат преди разрешението/стрийма — освобождаваме и не записваме
      if (session.cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      session.stream = stream;
      try {
        const mimeType = pickMimeType();
        const recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) session.chunks.push(event.data);
        };
        session.recorder = recorder;
        // Реалният старт е ТУК (след разрешението), не при натискането —
        // иначе времето до диалога се брои за запис.
        session.startedAt = Date.now();
        recorder.start();
        session.maxDurationTimer = window.setTimeout(() => {
          void finishSession(session).then((outcome) => {
            session.finished = outcome;
            setRecording(false);
          });
        }, MAX_DURATION_MS);
      } catch (error: unknown) {
        session.error = errorKindOf(error);
        releaseStream(session);
      }
    })();
  }, [finishSession]);

  const stop = useCallback(async (): Promise<RecordingOutcome> => {
    const session = sessionRef.current;
    if (!session) return { kind: "empty" };
    sessionRef.current = null;
    session.cancelled = true;
    setRecording(false);
    // Стриймът още се чака (диалогът за разрешение е отворен) — отказваме;
    // при следващото натискане разрешението вече е дадено.
    if (!session.recorder && !session.error) {
      return { kind: "empty" };
    }
    return finishSession(session);
  }, [finishSession]);

  const cancel = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    sessionRef.current = null;
    session.cancelled = true;
    setRecording(false);
    void finishSession(session);
  }, [finishSession]);

  // Броячът на секундите — само докато тече запис
  useEffect(() => {
    if (!recording) return;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    return () => window.clearInterval(timer);
  }, [recording]);

  // Демонтиране по средата на запис → освобождаваме микрофона. Важно за
  // iOS PWA: незатворен стрийм блокира следващия запис до рестарт.
  useEffect(() => {
    return () => {
      const session = sessionRef.current;
      if (!session) return;
      sessionRef.current = null;
      session.cancelled = true;
      if (session.maxDurationTimer != null)
        window.clearTimeout(session.maxDurationTimer);
      if (session.recorder && session.recorder.state !== "inactive")
        session.recorder.stop();
      releaseStream(session);
    };
  }, []);

  return { supported, recording, elapsedSeconds, start, stop, cancel };
};
